import React, { FC, useState, useRef, useEffect } from 'react';
import translate from '@translate';
import ProductsSelect, { ProductOptionType } from 'src/components/ProductsSelect';
import { checkFormValidity } from 'src/utils/formFunctions';
import Grid, { GridItem } from 'src/components/Grid';
import Button from 'src/components/Button';
import Typography from 'src/components/Typography';
import { sizes } from 'src/themes/default';
import Select from 'src/components/Select';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { ProductRelationEditKeysType } from './constants';
import { ProductListType, ProductRelation2Type, ProductType } from 'src/types/product';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { FormErrorsType } from '../../types/global';

const ALL_PRODUCTS_KEY = 'ALL_PRODUCTS_KEY';

type ProductRelationType = {
    ID_PRODUCT_1?: number;
    TYPE?: string;
    NAME?: string;
    SKU?: string;
    PRODUCT_TYPE?: string;
    ID?: number;
};

type NewProductRelationBlockPropsType = {
    productData?: ProductType | null;
    data?: ProductRelation2Type[];
    productRelationCodes: ConstantsCodeType[];
    onChange: (value: any) => void;
    disableEdit?: boolean;
    productsListData: ProductListType[] | null;
};

const initData: ProductRelationType = {};

const NewProductRelationBlock: FC<NewProductRelationBlockPropsType> = (props) => {
    const formRef = useRef<HTMLFormElement>(null);

    const [editData, setEditData] = useState<ProductRelationType>(initData);
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [allProductsOption, setAllProductsOption] = useState<ProductOptionType | undefined>();
    const [allProducts, setAllProducts] = useState<ProductListType[]>();
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    useEffect(() => {
        if (props.productsListData) {
            const newAllProducts = props.productData
                ? props.productsListData.filter((product) => product.ID !== props.productData?.ID)
                : props.productsListData;
            setAllProducts(newAllProducts);
            setAllProductsOption({
                label: translate('productDetail.allProductsOption', { length: newAllProducts.length }),
                value: ALL_PRODUCTS_KEY,
            });
        }
    }, [props.productsListData]);

    const changeValue = (key: ProductRelationEditKeysType, value: any, error: boolean) => {
        const newData = { ...editData };
        if (key === 'SKU') {
            if (value === ALL_PRODUCTS_KEY) {
                newData.SKU = value;
            } else {
                newData.NAME = value?.NAME;
                newData.SKU = value?.SKU;
                newData.PRODUCT_TYPE = value?.PRODUCT_TYPE;
                newData.ID_PRODUCT_1 = value.ID;
            }
        } else if (key === 'TYPE') {
            newData.TYPE = value;
        }
        const newErrors = { ...editErrors };
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleAddAllProducts = () => {
        setConfirmParams({
            title: translate('productDetail.confirmSetAllProductRelation'),
            content: translate('productDetail.confirmSetAllProductRelationText'),
            action: () => {
                props.onChange(
                    allProducts?.map((product, index) => ({
                        ID: -(index + 1),
                        ID_PRODUCT_1: product.ID,
                        NAME: product.NAME,
                        SKU: product.SKU,
                        PRODUCT_TYPE: product.PRODUCT_TYPE,
                        TYPE: editData.TYPE,
                        PRODUCT_1: product,
                    }))
                );
                setEditData(initData);
            },
            onCancel: () => {},
        });
    };

    const handleAddRow = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (editData.SKU === ALL_PRODUCTS_KEY) {
            handleAddAllProducts();
        } else if (!Object.keys(newFormErrors).length) {
            const sameItem = (props.data || []).find(
                (item) => item.ID_PRODUCT_1 === editData.ID_PRODUCT_1 && item.TYPE === editData.TYPE
            );
            if (!sameItem) {
                const newId = Math.min.apply(0, [0, ...(props.data || []).map((item: any): any => item.ID)]) - 1;
                props.onChange([...(props.data || []), { ...editData, ID: newId, PRODUCT_1: editData }]);
            }

            setEditData(initData);
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <Typography gutterBottom variant="h3">
                {translate('productDetail.addProductRelation')}
            </Typography>
            <form ref={formRef}>
                <Grid>
                    <GridItem cols={6}>
                        <ProductsSelect
                            value={editData.SKU}
                            error={editErrors.SKU}
                            idKey="SKU"
                            onChange={(value, error) => changeValue('SKU', value, error)}
                            name="SKU"
                            label={translate('productDetail.productRelationstable.nameSKU')}
                            fullWidth
                            disabled={props.disableEdit}
                            required
                            customFirstOption={allProductsOption}
                            extendsData={['PRODUCT_TYPE']}
                        />
                    </GridItem>
                    <GridItem cols={4}>
                        <Select
                            options={props.productRelationCodes}
                            value={editData.TYPE}
                            error={editErrors.TYPE}
                            onChange={(value, error) => changeValue('TYPE', value, error)}
                            name="TYPE"
                            label={translate('productDetail.productRelationstable.type')}
                            required
                            fullWidth
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <Button
                            onClick={handleAddRow}
                            disabled={props.disableEdit}
                            sx={{ marginTop: sizes.input_top_PD }}
                            mobileFullWidht
                        >
                            + {translate('productDetail.productRelationstable.action.add')}
                        </Button>
                    </GridItem>
                </Grid>
            </form>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default NewProductRelationBlock;
